// Selection-registry intent handlers (Stage 5).
//
// Owns the six `selection:*` intents that mutate `params.selections` +
// `params.selectionOrder` + `params.focusedSelectionId`, plus the
// per-primitive `<x>Domain` writes from `selection:set-domain`. After each
// mutation we sync the scene wireframes (via @scene/selections) and fan
// out re-render intents to whichever primitive's Domain currently
// references the touched selection.
//
// Sibling of @app/intents/selection.ts (the legacy probe cube). Stage 5
// makes registered selections primary but keeps the probe path intact —
// the probe cube updates on click-in-scene, not via these intents.

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import {
    createBoxSelection,
    createSphereSelection,
    removeSelection,
    setDomain,
    setFocus,
    updateSelection,
} from '@store/selections';
import {
    syncSelectionWireframes,
    removeSelectionWireframe,
} from '@scene/selections';
import { updateSlicePosition, applyDisplaySettings as slApplyDisplaySettings } from '@scene/slices';
import { applyVolumeDomainClip } from '@scene/volume';
import type { PrimitiveKey, SelectionId, State } from '@store/schema';
import type { Store } from '@store/store';
import type { ViewerContext } from '../context';

const DOMAIN_KEY_FOR: Record<PrimitiveKey, 'slicesDomain' | 'volumeDomain' | 'contourDomain' | 'fieldLinesDomain'> = {
    slices: 'slicesDomain',
    volume: 'volumeDomain',
    contour: 'contourDomain',
    fieldlines: 'fieldLinesDomain',
};

/** Primitives that currently scope to `id` — used to fan out re-renders. */
function primitivesReferencing(id: SelectionId): PrimitiveKey[] {
    const out: PrimitiveKey[] = [];
    for (const p of Object.keys(DOMAIN_KEY_FOR) as PrimitiveKey[]) {
        if (params[DOMAIN_KEY_FOR[p]] === id) out.push(p);
    }
    return out;
}

/** Re-render whichever primitives need to refresh after a domain or geometry edit. */
function rerenderPrimitives(viewer: ViewerContext, primitives: ReadonlyArray<PrimitiveKey>): void {
    for (const p of primitives) {
        if (p === 'slices') {
            // Re-rasterize all three slices so the mask kicks in immediately,
            // then refresh display settings so `material.transparent` picks
            // up the domain-masked state (alpha=0 pixels need transparency).
            for (const axis of ['XY', 'ZY', 'ZX'] as const) {
                const state = viewer.objects.slices.state[axis];
                if (!state || !state.mesh) continue;
                const pos = axis === 'XY' ? params.sliceZ : axis === 'ZY' ? params.sliceZY : params.sliceZX;
                updateSlicePosition(axis, viewer, params, pos);
            }
            slApplyDisplaySettings(viewer, params);
        } else if (p === 'volume') {
            // Cheap: just update the clip uniforms. The volume itself doesn't
            // need a tear-down/rebuild for a domain change.
            if (params.volumeEnabled) applyVolumeDomainClip(viewer, params);
        }
        // Contour + fieldlines: domain takes effect on the next Generate /
        // Trace (existing meshes/lines stay). No re-render fan-out here.
    }
}

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    /** Refresh the primitive pane folders so their Domain dropdowns
     *  pick up the new selection list. Single dispatch, multi-section
     *  payload — the orchestrator (src/ui/panel/sections.ts) walks the
     *  list in order. Selections folder first so its per-id ref map
     *  repopulates before any later refresh tries to read it. */
    const SELECTION_AWARE_SECTIONS = ['selection', 'slices', 'contour', 'volume', 'fieldlines', 'reductions'] as const;
    const rebuildAllDomainDropdowns = (): void => {
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: SELECTION_AWARE_SECTIONS });
    };

    offs.push(store.onIntent(INTENTS.SELECTION_CREATE_BOX, (p) => {
        const opts = {
            ...(p && p.lower ? { lower: p.lower } : {}),
            ...(p && p.upper ? { upper: p.upper } : {}),
            ...(p && typeof p.label === 'string' ? { label: p.label } : {}),
            ...(p && typeof p.focus === 'boolean' ? { focus: p.focus } : {}),
        };
        createBoxSelection(viewer.grid, opts);
        syncSelectionWireframes(viewer, params);
        rebuildAllDomainDropdowns();
    }));

    offs.push(store.onIntent(INTENTS.SELECTION_CREATE_SPHERE, (p) => {
        const opts = {
            ...(p && p.center ? { center: p.center } : {}),
            ...(p && typeof p.radius === 'number' ? { radius: p.radius } : {}),
            ...(p && p.keep ? { keep: p.keep } : {}),
            ...(p && typeof p.label === 'string' ? { label: p.label } : {}),
            ...(p && typeof p.focus === 'boolean' ? { focus: p.focus } : {}),
        };
        createSphereSelection(viewer.grid, opts);
        syncSelectionWireframes(viewer, params);
        rebuildAllDomainDropdowns();
    }));

    offs.push(store.onIntent(INTENTS.SELECTION_UPDATE, (p) => {
        if (!p || !p.id || !p.patch) return;
        const labelChanged = typeof (p.patch as { label?: string }).label === 'string';
        updateSelection(p.id, p.patch, viewer.grid);
        syncSelectionWireframes(viewer, params);
        rerenderPrimitives(viewer, primitivesReferencing(p.id));
        if (labelChanged) {
            // Refresh Selections folder + the Domain dropdowns that
            // reference this id by label.
            rebuildAllDomainDropdowns();
        }
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    offs.push(store.onIntent(INTENTS.SELECTION_DELETE, (p) => {
        if (!p || !p.id) return;
        const refs = primitivesReferencing(p.id);
        removeSelection(p.id);
        removeSelectionWireframe(viewer, p.id);
        // Domain reset to 'full' happened inside removeSelection — re-render
        // the ex-references so their mask drops.
        rerenderPrimitives(viewer, refs);
        rebuildAllDomainDropdowns();
    }));

    offs.push(store.onIntent(INTENTS.SELECTION_FOCUS, (p) => {
        const id = p && typeof p.id !== 'undefined' ? p.id : null;
        const prev = params.focusedSelectionId;
        setFocus(id);
        syncSelectionWireframes(viewer, params); // re-swap accent material
        // DOM-only marker swap: flips `data-focused` on the two affected
        // sub-folders and updates the Focus/Unfocus button label. Falls back
        // to a full rebuild inside the orchestrator if refs aren't populated.
        store.dispatch(INTENTS.UI_PANE_REBUILD_SECTION, { which: 'selection-focus', prevId: prev, newId: id });
    }));

    offs.push(store.onIntent(INTENTS.SELECTION_SET_DOMAIN, (p) => {
        if (!p || !p.primitive) return;
        const next = p.selectionId === 'full' ? 'full' : p.selectionId;
        setDomain(p.primitive, next);
        rerenderPrimitives(viewer, [p.primitive]);
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    return offs;
}
